import {
  Body,
  Controller,
  Stack,
  useFormContext,
  z,
} from '@qred/components-library';
import { ComboboxData } from '@qred/components-library/dist/lib/next/components/Combobox/Combobox.types';
import { OptionsFilter } from '@qred/components-library/dist/lib/next/components/Combobox/components/OptionsDropdown/OptionsDropdown';
import { FilterOptionsInput } from '@qred/components-library/dist/lib/next/components/Combobox/components/OptionsDropdown/defaultOptionsFilter';
import { AsyncSelect, SelectProps } from '@qred/components-library/next';
import { validateOrgNumber } from '@qred/shared-component-library/src/validators';
import debounce from 'lodash.debounce';
import React, { useCallback, useState } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { getMarketCanSearchCompanyByName } from '~/constants/markets';
import { logToSentry } from '~/helpers/loggers.helper';
import { normalizeOrgNumberHelper } from '~/helpers/normalizeOrgNumber.helper';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useTranslate from '~/hooks/useTranslate';
import { ICompanySearchResult } from '~/interfaces/shared';
import { apiGetCompanyInfoBySearch } from '~/services/api';
import { RootState } from '~/store';
import { trackCardApplicationActions } from '~/store/actions/analyticsActions';
import { setApiStatus } from '~/store/slices/cardApplication.slice';
import { ApiStatus } from '~/store/types/sharedTypes';

export const companySearchSchema = z.object({
  selectedCompany: z.string().min(1, { message: 'ValidationErrors.Required' }),
});

type CompanySearchForm = z.infer<typeof companySearchSchema>;

interface CompanySearchProps {
  defaultCompanyDetails: ICompanySearchResult | null;
  setCompanyDetails: (companyDetails: ICompanySearchResult | null) => void;
}

export const CompanySearch = ({
  defaultCompanyDetails,
  setCompanyDetails,
}: CompanySearchProps) => {
  const t = useTranslate();
  const dispatch = useAppDispatch();

  const market = useSelector((state: RootState) => state.intl.market);

  const {
    apiStatus: { companySearch: companySearchApiStatus },
  } = useSelector((state: RootState) => state.cardApplication);

  const { control, setValue } = useFormContext<CompanySearchForm>();

  const [requestHasBeenMade, setRequestHasBeenMade] = useState(false);

  const initialCompanies = defaultCompanyDetails
    ? [
        {
          companyName: defaultCompanyDetails.companyName || '',
          companyNumber: defaultCompanyDetails.companyNumber || '',
          companyType: defaultCompanyDetails.companyType || '',
        },
      ]
    : [];

  const [companies, setCompanies] = useState<ICompanySearchResult[]>(
    initialCompanies
  );
  const marketCanSearchByName = getMarketCanSearchCompanyByName(market);

  const debouncedCompanyFetch = useCallback(
    debounce(
      async (inputValue: string, callback: (data: ComboboxData) => void) => {
        dispatch(setApiStatus({ companySearch: ApiStatus.Started }));
        try {
          const result = await apiGetCompanyInfoBySearch(inputValue, market);

          setCompanies(
            result.map((company) => ({
              companyName: company.companyName,
              companyNumber: company.companyNumber ?? '',
              companyType: company.companyType,
            }))
          );

          callback(
            result.map((company) => ({
              label: company.companyName ?? '',
              value: company.companyNumber ?? '',
            }))
          );

          setRequestHasBeenMade(true);

          dispatch(setApiStatus({ companySearch: ApiStatus.Completed }));
        } catch (error: any) {
          setRequestHasBeenMade(true);
          if (error?.response?.status !== 404) {
            logToSentry(error, 'apiGetCompanyInfoBySearch');
          }
          dispatch(setApiStatus({ companySearch: ApiStatus.Failed }));
          setCompanies([]);
          callback([]);
        }
      },
      1000
    ),
    []
  );

  const clearSelectedCompany = (shouldValidate: boolean) => {
    setCompanies([]);
    setRequestHasBeenMade(false);

    setValue('selectedCompany', '', {
      shouldValidate,
    });
    setCompanyDetails(null);
  };

  const handleCompanySelect = (companyNumber: string) => {
    const selectedCompany = companies.find(
      (company) => company.companyNumber === companyNumber
    );

    if (!selectedCompany) {
      // TODO: Better way to do this?
      logToSentry(
        new Error(
          'No matching company found from API call to selected company'
        ),
        'companySearch'
      );
      return;
    }

    setValue('selectedCompany', selectedCompany.companyNumber, {
      shouldValidate: true,
    });

    setCompanyDetails(selectedCompany);

    setRequestHasBeenMade(false);
  };

  const getEmptyDropdownText = () => {
    if (companySearchApiStatus === ApiStatus.Started)
      return t('Loading') as string;

    return requestHasBeenMade && companySearchApiStatus !== ApiStatus.Idle
      ? (t('Onboarding.SelectCompanyNoSearchResultFound') as string)
      : (t('Onboarding.SelectCompanySearchValueMissingPrompt') as string);
  };

  const handleBlur = (field: ControllerRenderProps) => {
    dispatch(
      trackCardApplicationActions({
        actionName: `company_selector_${field.name}_change`,
      })
    );
    if (!field.value) {
      clearSelectedCompany(false);
    }
    field.onBlur();
  };

  const optionsFilter: OptionsFilter = (input: FilterOptionsInput) => {
    return input.options;
  };

  const loadData = (
    inputValue: string,
    callback: (data: ComboboxData) => void
  ) => {
    if (inputValue.trim().length < 2) {
      setRequestHasBeenMade(false);
      clearSelectedCompany(false);
      return callback([]);
    }

    const normalizedInputValue = normalizeOrgNumberHelper(inputValue, market);
    const isValidOrgNumberFormat = validateOrgNumber(
      normalizedInputValue,
      market
    );

    const searchValue = isValidOrgNumberFormat
      ? normalizedInputValue
      : inputValue;

    if (!marketCanSearchByName) {
      if (isValidOrgNumberFormat) {
        debouncedCompanyFetch(searchValue, callback);
      } else if (companies.length) {
        callback([
          {
            label: companies[0].companyName,
            value: companies[0].companyNumber,
          },
        ]);
      } else {
        callback([]);
      }
    } else {
      debouncedCompanyFetch(searchValue, callback);
    }
  };

  return (
    <Controller
      name="selectedCompany"
      control={control}
      render={({ field, fieldState }) => (
        <AsyncSelect
          label={t('Onboarding.SelectCompanySearchInputLabel')}
          defaultSearchValue={defaultCompanyDetails?.companyName || ''}
          defaultValue={field.value}
          defaultData={companies.map((company) => ({
            label: company.companyName,
            value: company.companyNumber,
          }))}
          filter={optionsFilter}
          loadData={loadData}
          onChange={(value) => {
            if (value) {
              handleCompanySelect(value);
            }
          }}
          error={fieldState.error?.message && t(fieldState.error?.message)}
          renderOption={renderSelectOption}
          onBlur={() => handleBlur(field)}
          nothingFoundMessage={getEmptyDropdownText()}
          searchable={true}
          clearable={true}
          // If we don't have allowDeselect set to false, if they change the search
          // and click on the same result, we actually deselect it
          allowDeselect={false}
          onClear={() => clearSelectedCompany(true)}
        />
      )}
    />
  );
};

const renderSelectOption: SelectProps['renderOption'] = ({ option }) => (
  <Stack spacing="xs">
    <Body size="lg" color="textDark.500">
      {option.label}
    </Body>
    <Body size="lg" color="textDark.200">
      {option.value}
    </Body>
  </Stack>
);
